import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';
import { commissioningQueryKey } from '@/features/commissioning/useCommissioning';
import { devicesQueryKey } from '@/features/inventory/useInventory';

/**
 * SLICE 14e — RECORD WHAT KIND OF INTERLOCK A DEVICE PHYSICALLY CARRIES (the
 * staff-facing half).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 🔴 SETTING `starter` SWITCHES OFF TWO SAFETY GUARDS. THIS FILE IS THE WRITE
 * PATH FOR THAT.
 * ═════════════════════════════════════════════════════════════════════════════
 * The command guard refuses a command above 5 km/h or on a fix older than 60 s.
 * Those checks exist to stop a command STALLING A MOVING VEHICLE. A `starter`
 * interlock opens the starter circuit — it prevents the NEXT start and cannot
 * touch a running engine — so on that hardware the guard skips both. Which means
 * recording `starter` about a relay that actually cuts FUEL would allow a
 * command that stalls a truck at speed, and no code can catch that: the only
 * evidence is a human looking at the wiring. Hence the mandatory inspection note
 * on `starter`, enforced by the server (422) and mirrored in the UI.
 *
 * 🔴 THE ASYMMETRY IS DELIBERATE. Clearing to `null`, and setting
 * `ignition`/`fuel`, RESTORE the guard — the safe direction — and carry no note.
 * Never make the safe action harder than the dangerous one, or people leave a
 * wrong `starter` in place rather than correct it.
 *
 * Mirrors the wire contract in `api/src/modules/admin/routes.ts` (SLICE 14e):
 *   PATCH /api/admin/devices/:deviceId/interlock
 *     body: { interlockType: 'starter'|'ignition'|'fuel'|null, note?: string }
 *     200:  { deviceId, imei, interlockType }
 *     400 invalid_body · 404 not_found · 422 note_required (starter without note)
 */

/** The three relay kinds the server accepts. `null` on the wire means "not
 *  recorded" — nobody has attested what is wired, and the FULL guard applies. */
export type InterlockType = 'starter' | 'ignition' | 'fuel';

/** The PATCH response. `interlockType` is what the row now holds — read it back
 *  rather than assuming the request landed unchanged. */
export interface SetInterlockResult {
  deviceId: string;
  imei: string;
  interlockType: InterlockType | null;
}

/**
 * The failures the dialog turns into a sentence a person can act on.
 *
 * `note_required` (422) is the one that matters: setting `starter` without an
 * inspection note is refused server-side, and the note is the whole worth of the
 * record. The client blocks it too, so a 422 here is the belt to that suspender.
 */
export type InterlockFailure = 'note_required' | 'invalid_body' | 'not_found' | 'unknown';

export function classifyInterlockError(error: unknown): InterlockFailure {
  if (error instanceof ApiError) {
    if (error.status === 422) return 'note_required';
    if (error.status === 400) return 'invalid_body';
    if (error.status === 404) return 'not_found';
  }
  return 'unknown';
}

/**
 * Set — or clear — a device's interlock type.
 *
 * 🔴 `note` IS OMITTED FROM THE BODY WHEN NOT SUPPLIED, never sent as `""` or
 * `null`. The server body is `.strict()` with `note: z.string().min(10).max(500)
 * .optional()`, so an empty-string note is a 400 (`invalid_body`), not an
 * accepted no-note. Only `starter` passes a note; the safe directions pass none.
 *
 * Invalidates the DEVICE REGISTER, which is where the per-device interlock cell
 * now lives (slice 14e-fix), so the screen reflects the new value the moment the
 * write lands. The commissioning bench is invalidated too — it still returns the
 * field, and a stale-cache surprise on either surface is cheaper to prevent than
 * to diagnose.
 */
export function useSetInterlock(): UseMutationResult<
  SetInterlockResult,
  Error,
  { deviceId: string; interlockType: InterlockType | null; note?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, interlockType, note }) =>
      apiJson<SetInterlockResult>(
        `/api/admin/devices/${encodeURIComponent(deviceId)}/interlock`,
        {
          method: 'PATCH',
          // note only when present — see the strict-body note above.
          body: JSON.stringify(
            note !== undefined && note !== '' ? { interlockType, note } : { interlockType },
          ),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: devicesQueryKey });
      void qc.invalidateQueries({ queryKey: commissioningQueryKey });
    },
  });
}
